import { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { createOrder, updateOrder, fetchPhoneOrderWarnings } from '../orders-controller/order-controller';
import { fetchSettings } from '../settings/controller/settings-controller';
import { promoCodeValidationService } from '../../../services/promoCodeValidationService';

export const useOrderForm = (navigate, isEditMode = false) => {
  // Form state
  const [formData, setFormData] = useState({
    reference: `CMD-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`,
    dateBonCommande: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
    etat: 'pending',
    platformId: '', // Keep this as platformId for the frontend components
    depotId: '',
    client: {
      firstName: '',
      lastName: '',
      address: '',
      city: 'TUN',
      phone: '',
      email: '',
      promoCode: ''
    },
    notes: '',
    paymentMethod: 'especes'
  });

  // Order items (products)
  const [orderItems, setOrderItems] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [settings, setSettings] = useState(null);
  const [showPhoneWarning, setShowPhoneWarning] = useState(false);
  const [phoneWarningOrders, setPhoneWarningOrders] = useState([]);
  const [hasWarnedForPhone, setHasWarnedForPhone] = useState(false);

  // PromoCode state - MODIFIED for edit mode
  const [promoCodeState, setPromoCodeState] = useState({
    isValidating: false,
    isValid: false,
    discount: 0,
    validatedCode: null,
    error: null,
    isDisabled: isEditMode // disable in edit mode
  });

  // NEW - Store original promo code for edit mode
  const [originalPromoCode, setOriginalPromoCode] = useState('');

  // Calculate totals - MODIFIED to include promo discount
  const [totals, setTotals] = useState({
    subtotal: 0,
    tva: 0,
    total: 0,
    remise: 0,
    deliveryFee: 0, // Initialize with 0, will be updated when settings are fetched
    promoDiscount: 0 // promo code discount
  });

  // Status options
  const statusOptions = [
    { value: 'pending', label: 'En attente', color: 'yellow' },
    { value: 'confirmed', label: 'Confirmé', color: 'green' },
  ];

  // Fetch settings for delivery fee - SIMPLIFIED
  useEffect(() => {
    const getSettings = async () => {
      try {
        const settingsData = await fetchSettings();
        setSettings(settingsData);
        // That's it! Let calculateOrderTotals handle the delivery fee calculation
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    
    getSettings();
  }, []);

  // Update totals when order items change OR when promo code state changes - KEEP THIS
  useEffect(() => {
    
    calculateOrderTotals();
  }, [orderItems, settings?.fraisDeLivraison, promoCodeState.isValid, promoCodeState.discount]); // More specific dependencies

  // PromoCode validation function for create mode
  const validatePromoCode = async (code) => {
    if (!code || code.trim() === '') {
      setPromoCodeState(prev => ({
        ...prev,
        isValidating: false,
        isValid: false,
        discount: 0,
        validatedCode: null,
        error: null
      }));
      return;
    }

    // In edit mode, don't allow validation of new codes
    if (isEditMode) {
      return;
    }

    setPromoCodeState(prev => ({
      ...prev,
      isValidating: true,
      error: null
    }));

    try {
      const result = await promoCodeValidationService.checkPromoCode(code.trim());
      
      if (result.success) {
        setPromoCodeState(prev => ({
          ...prev,
          isValidating: false,
          isValid: true,
          discount: result.data.montant || 0,
          validatedCode: {montant : result.data, code: code.trim()},
          error: null
        }));
      } else {
        setPromoCodeState(prev => ({
          ...prev,
          isValidating: false,
          isValid: false,
          discount: 0,
          validatedCode: null,
          error: result.error
        }));
      }
    } catch (error) {
      setPromoCodeState(prev => ({
        ...prev,
        isValidating: false,
        isValid: false,
        discount: 0,
        validatedCode: null,
        error: 'Erreur lors de la validation du code promo'
      }));
    }
  };

  // NEW - Function to fetch and set promo code data from API for edit mode - SIMPLIFIED
  const setExistingPromoCodeData = async (promoCode, promoDiscountFromDB = 0) => {
    
    setOriginalPromoCode(promoCode || '');
    
    if (promoCode && promoCode.trim() !== '') {
      setPromoCodeState(prev => ({
        ...prev,
        isValidating: true,
        error: null,
        isDisabled: true
      }));

      try {
        const result = await promoCodeValidationService.checkPromoCode(promoCode.trim());
        
        if (result.success) {
          const actualDiscount = result.data.montant || 0;
          
          // SIMPLE STATE UPDATE - LET useEffect HANDLE THE CALCULATION
          setPromoCodeState({
            isValidating: false,
            isValid: true,
            discount: actualDiscount,
            validatedCode: {montant : result.data, code: promoCode.trim()},
            error: null,
            isDisabled: true
          });
          
          return actualDiscount;
          
        } else {
          setPromoCodeState({
            isValidating: false,
            isValid: false,
            discount: 0,
            validatedCode: null,
            error: `Code promo non valide: ${result.error}`,
            isDisabled: true
          });
          return 0;
        }
      } catch (error) {
        console.error('Edit mode: Error fetching promo code:', error);
        const fallbackDiscount = promoDiscountFromDB || 0;
        
        setPromoCodeState({
          isValidating: false,
          isValid: fallbackDiscount > 0,
          discount: fallbackDiscount,
          validatedCode: { code: promoCode, montant: fallbackDiscount },
          error: 'Impossible de vérifier le code promo',
          isDisabled: true
        });
        
        return fallbackDiscount;
      }
    } else {
      setPromoCodeState({
        isValidating: false,
        isValid: false,
        discount: 0,
        validatedCode: null,
        error: null,
        isDisabled: true
      });
      return 0;
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle nested client properties
    if (name.startsWith('client.')) {
      const clientField = name.split('.')[1];
      
      // Prevent promo code changes in edit mode
      if (clientField === 'promoCode' && isEditMode) {
        return;
      }
      
      setFormData(prev => {
        const updatedFormData = {
          ...prev,
          client: {
            ...prev.client,
            [clientField]: value
          }
        };
        
        return updatedFormData;
      });

      // If promo code field changed and not in edit mode, reset promo code state
      if (clientField === 'promoCode' && !isEditMode) {
        setPromoCodeState(prev => ({
          ...prev,
          isValidating: false,
          isValid: false,
          discount: 0,
          validatedCode: null,
          error: null
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear error for the updated field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  // Handle promo code blur (disabled in edit mode)
  const handlePromoCodeBlur = () => {
    if (isEditMode) {
      return;
    }
    
    const promoCode = formData.client.promoCode;
    if (promoCode && promoCode.trim() !== '') {
      validatePromoCode(promoCode);
    }
  };

  // Handle status change
  const handleStatusChange = (status) => {
    setFormData(prev => ({
      ...prev,
      etat: status
    }));
  };

  // Handle adding a new item to the order
  const handleAddItem = (newItem) => {
    setOrderItems(prevItems => [...prevItems, newItem]);
  };

  // Handle updating an existing item
  const handleUpdateItem = (itemId, updatedItem) => {
    setOrderItems(prevItems => 
      prevItems.map(item => 
        item.id === itemId ? updatedItem : item
      )
    );
  };

  // Handle removing an item
  const handleRemoveItem = (itemId) => {
    setOrderItems(prevItems => 
      prevItems.filter(item => item.id !== itemId)
    );
  };

  // Calculate order totals - MODIFIED to apply promo discount on TTC then recalculate HT and TVA
  const calculateOrderTotals = useCallback(() => {
    let subtotalHT = 0;
    let tvaTotal = 0;
    let itemRemiseTotal = 0;

    const deliveryFee = settings?.fraisDeLivraison || 0;

    // Calculate item totals (KEEP EXISTING LOGIC)
    orderItems.forEach(item => {
      const unitHtPrice = item.price / (1 + item.tva / 100);
      const itemHT = unitHtPrice * item.quantity;
      subtotalHT += itemHT;
      
      const itemRemise = itemHT * (item.remise / 100);
      itemRemiseTotal += itemRemise;
      
      const itemTva = (itemHT - itemRemise) * (item.tva / 100);
      tvaTotal += itemTva;
    });

    // Calculate TTC after item remises
    const subtotalHTAfterItemRemise = subtotalHT - itemRemiseTotal;
    const subtotalTTCAfterItemRemise = subtotalHTAfterItemRemise + tvaTotal;

    // Apply promo discount to TTC total
    const currentPromoDiscount = (promoCodeState?.isValid && promoCodeState?.discount) ? promoCodeState.discount : 0;
    const discountedTTC = Math.max(0, subtotalTTCAfterItemRemise - currentPromoDiscount);
    
    // Recalculate HT and TVA from discounted TTC
    const newHT = discountedTTC / 1.19; // Assuming 19% TVA
    const newTVA = discountedTTC - newHT;
    
    // Total remise includes both item remises and promo discount
    const totalRemise = itemRemiseTotal + currentPromoDiscount;
    
    // Final total = discounted TTC + delivery
    const total = discountedTTC + parseFloat(deliveryFee);

    const newTotals = {
      subtotalHT,
      remise: totalRemise,
      tva: newTVA, // New TVA calculated from discounted TTC
      total,
      deliveryFee,
      promoDiscount: currentPromoDiscount,
      finalHT: newHT, // New HT calculated from discounted TTC
      finalTTC: discountedTTC // TTC after promo discount
    };

    setTotals(newTotals);
    
  }, [orderItems, settings?.fraisDeLivraison, promoCodeState?.isValid, promoCodeState?.discount]);

  // Update the prepareOrderData function
  const prepareOrderData = (orderId = null) => {
    const deliveryFee = totals.deliveryFee !== undefined ? 
      totals.deliveryFee : 
      (settings?.fraisDeLivraison || 0);

    // Use the recalculated HT and TVA from discounted TTC
    const finalHT = totals.finalHT || 0;
    const finalTVA = totals.tva || 0;

    console.log('this is the status : ', formData.etat);
    const entet = {
      dateBonCommande: formData.dateBonCommande,
      reference: formData.reference,
      utilisateurId: 1,
      totalTTC: Number(totals.total.toFixed(2)),
      totalHt: Number(finalHT.toFixed(2)), // HT calculated from discounted TTC
      totalTva: Number(finalTVA.toFixed(2)), // TVA calculated from discounted TTC
      totalRemise: Number(totals.remise.toFixed(2)),
      codePromo: promoCodeState.isValid ? formData.client.promoCode : "",
      promoDiscount: Number(totals.promoDiscount.toFixed(2)),
      source: "web_admin",
      etat: formData.etat === 'abandoned' ? 'pending' : formData.etat,
      fraisDeLivraison: parseFloat(deliveryFee || 0).toFixed(2),
      plateformId: formData.platformId ? parseInt(formData.platformId, 10) : null,
      depotId: formData.depotId ? parseInt(formData.depotId, 10) : null,
      clientFirstName: formData.client.firstName || "",
      clientLastName: formData.client.lastName || "",
      clientAdresse: formData.client.address || "",
      clientNumber: formData.client.phone || "",
      ville: formData.client.city || ""
    };

    // If we're editing, include the order ID
    if (orderId) {
      entet.id = parseInt(orderId);
    }

    // Prepare order items (line) - Apply proportional promo discount
    const line = orderItems.map(item => {
      // Calculate the unit HT price using the formula
      const unitHtPrice = item.price / (1 + item.tva / 100);
      
      // Calculate HT total for this item
      const itemHT = unitHtPrice * item.quantity;
      
      // Calculate discount on the HT price (item-level only)
      const itemRemise = itemHT * (item.remise / 100);
      
      // Calculate subtotal HT after item remise only
      const itemHTAfterRemise = itemHT - itemRemise;

      // Calculate TVA on the HT price after remise
      const itemTva = itemHTAfterRemise * (item.tva / 100);

      // Calculate item TTC after item remise
      const itemTTCAfterItemRemise = itemHTAfterRemise + itemTva;
      
      // Calculate proportional promo discount for this item
      const totalTTCAfterItemRemises = orderItems.reduce((sum, orderItem) => {
        const orderItemHT = (orderItem.price / (1 + orderItem.tva / 100)) * orderItem.quantity;
        const orderItemRemise = orderItemHT * (orderItem.remise / 100);
        const orderItemHTAfterRemise = orderItemHT - orderItemRemise;
        const orderItemTVA = orderItemHTAfterRemise * (orderItem.tva / 100);
        return sum + orderItemHTAfterRemise + orderItemTVA;
      }, 0);
      
      const proportionalPromoDiscount = totalTTCAfterItemRemises > 0 ? 
        (itemTTCAfterItemRemise / totalTTCAfterItemRemises) * totals.promoDiscount : 0;
      
      // Apply proportional promo discount to item TTC
      const itemTTCAfterPromo = Math.max(0, itemTTCAfterItemRemise - proportionalPromoDiscount);
      
      // Recalculate HT and TVA from discounted TTC
      const finalItemHT = itemTTCAfterPromo / (1 + item.tva / 100);
      const finalItemTVA = itemTTCAfterPromo - finalItemHT;
      
      // Total remise for this item (item remise + proportional promo)
      const totalItemRemise = itemRemise + proportionalPromoDiscount;
      
      const lineItem = {
        productVariantId: item.variantId || null,
        productId: item.productId || null,
        quantite: item.quantity,
        prixUnitaire: Number(item.price.toFixed(2)),
        totalTTC: Number(itemTTCAfterPromo.toFixed(2)), // TTC after all discounts
        totalHt: Number(finalItemHT.toFixed(2)), // HT calculated from discounted TTC
        totalTva: Number(finalItemTVA.toFixed(2)), // TVA calculated from discounted TTC
        totalRemise: Number(totalItemRemise.toFixed(2)), // Total remise including promo
        tauxTva: Number(item.tva),
        tauxRemise: Number(item.remise)
      };

      // If we're editing and the item has an ID, include it
      if (isEditMode && item.dbId) {
        lineItem.id = item.dbId;
      }

      return lineItem;
    });

    return {
      entet,
      line
    };
  };

  // NEW - Add this function before the handleSubmit function
  const validateForm = () => {
    const newErrors = {};

    // Validate required fields
    if (!formData.reference.trim()) {
      newErrors.reference = 'La référence est requise';
    }

    if (!formData.dateBonCommande) {
      newErrors.dateBonCommande = 'La date de commande est requise';
    }

    if (!formData.client.firstName.trim()) {
      newErrors['client.firstName'] = 'Le prénom est requis';
    }

    if (!formData.client.lastName.trim()) {
      newErrors['client.lastName'] = 'Le nom est requis';
    }

    if (!formData.client.phone.trim()) {
      newErrors['client.phone'] = 'Le téléphone est requis';
    } else if (!/^\d{8}$/.test(formData.client.phone.trim())) {
      newErrors['client.phone'] = 'Le téléphone doit contenir 8 chiffres';
    }

    if (!formData.client.address.trim()) {
      newErrors['client.address'] = 'L\'adresse est requise';
    }

    if (!formData.client.city.trim()) {
      newErrors['client.city'] = 'La ville est requise';
    }

    // Validate email if provided
    if (formData.client.email && formData.client.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.client.email.trim())) {
        newErrors['client.email'] = 'Format d\'email invalide';
      }
    }

    // Validate order items
    if (orderItems.length === 0) {
      newErrors.orderItems = 'Au moins un produit est requis';
    }

    // Validate each order item
    orderItems.forEach((item, index) => {
      if (!item.productId && !item.variantId) {
        newErrors[`orderItem_${index}_product`] = 'Le produit est requis';
      }
      if (!item.quantity || item.quantity <= 0) {
        newErrors[`orderItem_${index}_quantity`] = 'La quantité doit être supérieure à 0';
      }
      if (!item.price || item.price <= 0) {
        newErrors[`orderItem_${index}_price`] = 'Le prix doit être supérieur à 0';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Form submission - MODIFIED to validate promo code availability before submission
  const handleSubmit = async (e, orderId = null) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Only check warning if not already warned for this phone
    if (!hasWarnedForPhone) {
      try {
        const phone = formData.client.phone;
        const warningOrders = await fetchPhoneOrderWarnings(phone);
        if (warningOrders && warningOrders.length > 0) {
          setPhoneWarningOrders(warningOrders);
          setShowPhoneWarning(true);
          setHasWarnedForPhone(true);
          return; // Do not proceed, wait for user to close modal and resubmit
        }
      } catch (err) {
        // Optionally handle API error, but allow order creation
        console.error('Warning check failed:', err);
      }
    }

    setIsSubmitting(true);
    try {
      // ADDED: Check promo code availability before submission
      if (promoCodeState.isValid && promoCodeState.validatedCode) {
        try {
          console.log('Checking promo code availability for:', promoCodeState);
          const checkoutResult = await promoCodeValidationService.checkPromoCode(promoCodeState.validatedCode.code);
          
          if (!checkoutResult.success && !isEditMode) {
            // Promo code is no longer available
            setErrors(prev => ({
              ...prev,
              submit: `Code promo non disponible: ${checkoutResult.error}`
            }));
            
            // Reset promo code state
            setPromoCodeState({
              isValidating: false,
              isValid: false,
              discount: 0,
              validatedCode: null,
              error: checkoutResult.error
            });
            
            return; // Stop submission
          }
        } catch (error) {
          console.error('Error checking promo code availability:', error);
          setErrors(prev => ({
            ...prev,
            submit: 'Erreur lors de la vérification du code promo. Veuillez réessayer.'
          }));
          return; // Stop submission
        }
      }
      console.log('Submitting order with data:', formData, orderItems, totals);
      const orderData = prepareOrderData(orderId);

      let response;
      
      // Call the appropriate API method based on whether we're creating or updating
      if (isEditMode && orderId) {
        orderData.entet.id = orderId;
        response = await createOrder(orderData); // Use updateOrder for edit mode
      } else {
        response = await createOrder(orderData);
      }
      
      // Redirect to orders list
      if (navigate) {
        navigate('/admin/orders');
      }
      
    } catch (error) {
      let errorMessage = `${error.response?.data?.errorMessage || error.message}`;
      
      // Show error message
      setErrors(prev => ({
        ...prev,
        submit: errorMessage
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Function to update totals from child components
  const updateTotals = (newTotals) => {
    setTotals(newTotals);
  };

  // Return all the functions and state
  return {
  formData,
  setFormData,
  orderItems,
  setOrderItems,
  errors,
  totals,
  updateTotals,
  statusOptions,
  isSubmitting,
  promoCodeState,
  handleInputChange,
  handleStatusChange,
  handleAddItem,
  handleUpdateItem,
  handleRemoveItem,
  handleSubmit,
  handlePromoCodeBlur,
  validatePromoCode,
  setExistingPromoCodeData, // This now fetches from API
  isEditMode,
  showPhoneWarning,
  setShowPhoneWarning,
  phoneWarningOrders
};
};